/**
 * PORTAL CLIENTE v2 — INICIO: EL ESTADO DE TU SERVICIO
 *
 * El cliente no gestiona la limpieza: entra a comprobar que se hizo, que está verificada y qué merece su
 * atención. Primero la conclusión («12 de 15 limpiezas realizadas»), después las cifras y, al final, lo que
 * requiere atención con enlace directo a cada cosa. Cada cifra lleva al listado de OT ya filtrado.
 *
 * Todo sale de `GET /api/portal/servicio` (`servicio-portal.ts`, la única definición de cada indicador); los
 * centros, de `GET /api/portal/centros` (sólo los suyos — nunca hay que elegir cliente). Centro y periodo viven
 * en la URL. Sin costes, horas internas, asignaciones ni productividad: no son superficie de cliente.
 *
 * PC-v2.2-B: qué cifras se enseñan lo decide `GET /api/portal/indicadores` (configuración del cliente y, con un
 * centro elegido, la de ese centro). Una oculta no deja hueco; los esenciales están siempre. La conclusión y
 * «Requieren atención» cuentan sólo lo que se enseña. Es presentación: los cálculos no cambian.
 */
import { useId, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { api, type CocientePortal, type GrupoOtPortal, type OtAtencionPortal, type ResumenServicioPortal } from '../api.ts';
import { Cargando, Error as ErrorCaja, EstadoNc, EstadoOt, fecha, hoyLocal, useDatos } from '../componentes/base.tsx';
import { EstadoIncidenciaEtiqueta } from '../componentes/calidad.tsx';
import { IND, useIndicadoresPortal } from '../componentes/indicadores-portal.ts';
import { useSesion } from '../contexto.tsx';
import { useListaUrl } from '../listaUrl.ts';

export type PeriodoPortal = 'hoy' | '7' | '30' | 'mes';

export const PERIODOS_PORTAL: readonly { readonly clave: PeriodoPortal; readonly texto: string }[] = [
  { clave: 'hoy', texto: 'Hoy' },
  { clave: '7', texto: 'Últimos 7 días' },
  { clave: '30', texto: 'Últimos 30 días' },
  { clave: 'mes', texto: 'Este mes' },
];

export function rangoPeriodoPortal(p: string, ahora: Date = new Date()): { desde: string; hasta: string } {
  const hasta = hoyLocal(0, ahora);
  if (p === 'hoy') return { desde: hasta, hasta };
  if (p === '30') return { desde: hoyLocal(-29, ahora), hasta };
  if (p === 'mes') return { desde: `${hasta.slice(0, 8)}01`, hasta };
  return { desde: hoyLocal(-6, ahora), hasta };
}

export function textoPorcentaje(c: CocientePortal): string {
  return c.pct === null ? '—' : `${String(c.pct).replace('.', ',')} %`;
}

/** Centros del cliente para los filtros del portal. */
export function useCentrosPortal(): { id: string; nombre: string }[] {
  return useDatos(() => api.get<{ filas: { id: string; nombre: string }[] }>('/api/portal/centros'), []).datos?.filas ?? [];
}

const DEFAULTS = { idCentro: '', periodo: '7' } as const;

export function SeccionServicioCliente(): ReactNode {
  const idBase = useId();
  const { puede } = useSesion();
  const lista = useListaUrl(DEFAULTS);
  const idCentro = lista.valor('idCentro');
  const periodo = lista.valor('periodo');
  const { desde, hasta } = rangoPeriodoPortal(periodo);
  const centros = useCentrosPortal();
  const consulta = new URLSearchParams({ desde, hasta });
  if (idCentro !== '') consulta.set('idCentro', idCentro);
  const datos = useDatos(() => api.get<ResumenServicioPortal>(`/api/portal/servicio?${consulta.toString()}`), [consulta.toString()]);
  //  Nunca se pinta la respuesta de otro ámbito mientras llega la nueva.
  const vigente = datos.datos !== null && datos.datos.periodo.desde === desde && datos.datos.periodo.hasta === hasta
    && (datos.datos.idCentro ?? '') === idCentro ? datos.datos : null;
  //  Ni las cifras ni su configuración de otro centro: se pinta cuando las dos son de ESTE.
  const indicadores = useIndicadoresPortal(idCentro);
  const v = indicadores.visibles;
  const r = v === null ? null : vigente;
  const conCalidad = puede('calidad:leer_propia');

  const aLista = (grupo?: GrupoOtPortal): string => {
    const q = new URLSearchParams({ fechaDesde: desde, fechaHasta: hasta });
    if (idCentro !== '') q.set('idCentro', idCentro);
    if (grupo !== undefined) q.set('grupo', grupo);
    return `/mis-ots?${q.toString()}`;
  };
  const textoPeriodo = PERIODOS_PORTAL.find((p) => p.clave === periodo)?.texto ?? '';

  return (
    <section aria-labelledby={`${idBase}-t`} className="portal-servicio">
      <div className="panel">
        <div className="panel-cabecera">
          <h2 id={`${idBase}-t`}>Estado de tu servicio</h2>
        </div>
        <div className="panel-cuerpo">
          <div className="fila portal-filtros">
            <div className="campo">
              <label htmlFor={`${idBase}-centro`}>Centro</label>
              <select id={`${idBase}-centro`} value={idCentro} onChange={(e) => lista.fijar('idCentro', e.target.value)}>
                <option value="">Todos tus centros</option>
                {centros.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
              </select>
            </div>
            <div className="campo">
              <label htmlFor={`${idBase}-periodo`}>Periodo</label>
              <select id={`${idBase}-periodo`} value={periodo} onChange={(e) => lista.fijar('periodo', e.target.value)}>
                {PERIODOS_PORTAL.map((p) => <option key={p.clave} value={p.clave}>{p.texto}</option>)}
              </select>
            </div>
          </div>

          {r === null && datos.error === null && indicadores.error === null && <Cargando texto="Calculando el estado del servicio…" />}
          {datos.error !== null && <ErrorCaja error={datos.error} reintentar={datos.recargar} />}
          {indicadores.error !== null && <ErrorCaja error={indicadores.error} reintentar={indicadores.recargar} />}
          {r !== null && v !== null && <Conclusion r={r} v={v} conCalidad={conCalidad} textoPeriodo={textoPeriodo} />}
        </div>
      </div>

      {r !== null && v !== null && (
        <>
          <ul className="portal-kpis" aria-label="Indicadores del servicio">
            {v.has(IND.inicio.previstas) && <Kpi a={aLista()} titulo="Previstas" valor={String(r.ot.previstas)} detalle="órdenes de trabajo" />}
            {v.has(IND.inicio.realizadas) && <Kpi a={aLista('REALIZADAS')} titulo="Realizadas" valor={String(r.ot.realizadas)} detalle={`${String(r.tareas.realizadas)} de ${String(r.tareas.total)} tareas`} tono="exito" />}
            {v.has(IND.inicio.pendientes) && <Kpi a={aLista('PENDIENTES')} titulo="Pendientes" valor={String(r.ot.pendientes)}
              detalle={r.ot.vencidas > 0 ? `${String(r.ot.vencidas)} fuera de plazo` : 'en plazo'} tono={r.ot.vencidas > 0 ? 'aviso' : undefined} />}
            {v.has(IND.inicio.conIncidencia) && <Kpi a={aLista('CON_INCIDENCIA')} titulo="Con incidencia" valor={String(r.ot.conIncidencia)}
              detalle={r.tareas.noOk > 0 ? `${String(r.tareas.noOk)} tarea(s) No OK` : 'tareas No OK o avisos'} tono={r.ot.conIncidencia > 0 ? 'error' : undefined} />}
            {v.has(IND.inicio.verificadas) && <Kpi a={aLista('VERIFICADAS')} titulo="Verificadas" valor={String(r.ot.verificadas)} detalle={`${String(r.tareas.verificadas)} tareas verificadas`} tono="exito" />}
            {v.has(IND.inicio.cumplimiento) && <Kpi titulo="Cumplimiento" valor={textoPorcentaje(r.cumplimiento)}
              detalle={r.cumplimiento.pct === null ? 'Todavía no hay nada que debiera estar hecho' : `${String(r.cumplimiento.n)} de ${String(r.cumplimiento.total)} que ya tocaban`} />}
            {v.has(IND.inicio.verificacion) && <Kpi titulo="Verificación" valor={textoPorcentaje(r.verificacion)}
              detalle={r.verificacion.pct === null ? 'Aún no hay limpiezas realizadas' : `${String(r.verificacion.n)} de ${String(r.verificacion.total)} realizadas`} />}
          </ul>
          <RequierenAtencion r={r} v={v} aLista={aLista} conCalidad={conCalidad} />
          <div className="fila portal-accesos">
            <Link to={aLista()} className="etiqueta info">Ver todas las órdenes</Link>
            <Link to="/portal/planner" className="etiqueta info">Ver el calendario</Link>
            {puede('calidad:leer_propia') && <Link to="/portal/calidad?tab=indicadores" className="etiqueta info">Ver Calidad</Link>}
          </div>
        </>
      )}
    </section>
  );
}

/**
 * Lo que «Requieren atención» enseña con la configuración vigente (null = no se enseña): la conclusión cuenta lo mismo.
 * `hayOcultos`: hay asuntos abiertos en un apartado oculto. No se enseñan ni se cuentan, pero entonces la pantalla no
 * puede afirmar «Todo en orden» (PC22B-ATENCION-OCULTA).
 */
function atencionVisible(r: ResumenServicioPortal, v: ReadonlySet<string>, conCalidad: boolean) {
  const { vencidas, noOk, incidencias, noConformidades } = r.atencion;
  const ocultos = (conCalidad && !v.has(IND.inicio.incidencias) ? incidencias?.total ?? 0 : 0)
    + (conCalidad && !v.has(IND.inicio.noConformidades) ? noConformidades?.total ?? 0 : 0);
  return {
    vencidas: v.has(IND.inicio.fueraDePlazo) ? vencidas : null,
    noOk: v.has(IND.inicio.tareasNoOk) ? noOk : null,
    incidencias: conCalidad && v.has(IND.inicio.incidencias) ? incidencias : null,
    noConformidades: conCalidad && v.has(IND.inicio.noConformidades) ? noConformidades : null,
    hayOcultos: ocultos > 0,
  };
}

function Conclusion({ r, v, conCalidad, textoPeriodo }: { r: ResumenServicioPortal; v: ReadonlySet<string>; conCalidad: boolean; textoPeriodo: string }): ReactNode {
  const a = atencionVisible(r, v, conCalidad);
  const atencion = (a.vencidas?.total ?? 0) + (a.noOk?.total ?? 0) + (a.incidencias?.total ?? 0) + (a.noConformidades?.total ?? 0);
  if (r.ot.previstas === 0) {
    return <p className="portal-conclusion">{textoPeriodo}: no hay limpiezas previstas.</p>;
  }
  return (
    <div className="portal-conclusion" role="status">
      <p>
        <strong>{textoPeriodo}: {r.ot.realizadas} de {r.ot.previstas} limpiezas realizadas</strong>
        {v.has(IND.inicio.verificadas) && <>{' · '}{r.ot.verificadas} verificadas</>}
      </p>
      {atencion === 0
        ? !a.hayOcultos && <span className="etiqueta exito">Todo en orden</span>
        : <span className="etiqueta aviso">{atencion === 1 ? '1 asunto requiere tu atención' : `${String(atencion)} asuntos requieren tu atención`}</span>}
    </div>
  );
}

function Kpi({ a, titulo, valor, detalle, tono }: { a?: string; titulo: string; valor: string; detalle: string; tono?: 'exito' | 'aviso' | 'error' }): ReactNode {
  const cuerpo = (
    <>
      <span className="etiqueta-dato">{titulo}</span>
      <span className="portal-kpi-cifra">{valor}</span>
      <span className="pequena tenue">{detalle}</span>
    </>
  );
  return (
    <li className={`portal-kpi${tono === undefined ? '' : ` tono-${tono}`}`}>
      {a === undefined ? <div className="portal-kpi-cuerpo">{cuerpo}</div> : <Link to={a} className="portal-kpi-cuerpo" aria-label={`${titulo}: ${valor}. Ver las órdenes`}>{cuerpo}</Link>}
    </li>
  );
}

function ListaOtsAtencion({ ots, detalle }: { ots: readonly OtAtencionPortal[]; detalle: (o: OtAtencionPortal) => string }): ReactNode {
  return (
    <ul className="portal-atencion-lista">
      {ots.map((o) => (
        <li key={o.id}>
          <Link to={`/mis-ots/${o.id}`}>{o.codigo}</Link>
          <span>{o.tarea ?? 'Sin tareas'}</span>
          <span className="pequena tenue">{o.centro} · {detalle(o)}</span>
          <EstadoOt estado={o.estado} size="sm" />
        </li>
      ))}
    </ul>
  );
}

function RequierenAtencion({ r, v, aLista, conCalidad }: {
  r: ResumenServicioPortal; v: ReadonlySet<string>; aLista: (g?: GrupoOtPortal) => string; conCalidad: boolean;
}): ReactNode {
  const { vencidas, noOk, incidencias, noConformidades, hayOcultos } = atencionVisible(r, v, conCalidad);
  const nada = (vencidas?.total ?? 0) + (noOk?.total ?? 0) + (incidencias?.total ?? 0) + (noConformidades?.total ?? 0) === 0;
  return (
    <div className="panel">
      <div className="panel-cabecera"><h2>Requieren atención</h2></div>
      <div className="panel-cuerpo">
        {nada ? (
          hayOcultos
            ? <p className="pequena suave" style={{ margin: 0 }}>No hay asuntos pendientes en los apartados que se muestran aquí.</p>
            : <p className="nota exito" style={{ margin: 0 }}>Nada requiere tu atención ahora mismo.</p>
        ) : (
          <div className="portal-atencion">
            {vencidas !== null && vencidas.total > 0 && (
              <div>
                <h3>Fuera de plazo <span className="etiqueta aviso">{vencidas.total}</span></h3>
                <ListaOtsAtencion ots={vencidas.ots} detalle={(o) => `debía estar hecha el ${fecha(o.fechaLimite)}`} />
                {vencidas.total > vencidas.ots.length && <Link to={aLista('VENCIDAS')} className="pequena">Ver las {vencidas.total}</Link>}
              </div>
            )}
            {noOk !== null && noOk.total > 0 && (
              <div>
                <h3>Tareas No OK <span className="etiqueta error">{noOk.total}</span></h3>
                <ListaOtsAtencion ots={noOk.ots} detalle={(o) => `${String(o.tareasNoOk)} tarea(s) No OK · ${fecha(o.fecha)}`} />
                {noOk.total > noOk.ots.length && <Link to={aLista('CON_INCIDENCIA')} className="pequena">Ver todas</Link>}
              </div>
            )}
            {incidencias !== null && incidencias.total > 0 && (
              <div>
                <h3>Incidencias abiertas <span className="etiqueta aviso">{incidencias.total}</span></h3>
                <ul className="portal-atencion-lista">
                  {incidencias.filas.map((i) => (
                    <li key={i.id}>
                      <Link to={`/portal/calidad/incidencias/${i.id}`}>{i.codigo}</Link>
                      <span>{i.titulo}</span>
                      <span className="pequena tenue">{i.centro} · {fecha(i.creadaEn.slice(0, 10))}</span>
                      <EstadoIncidenciaEtiqueta estado={i.estado} />
                    </li>
                  ))}
                </ul>
                {incidencias.total > incidencias.filas.length && <Link to="/portal/calidad?tab=incidencias" className="pequena">Ver todas</Link>}
              </div>
            )}
            {noConformidades !== null && noConformidades.total > 0 && (
              <div>
                <h3>No conformidades abiertas <span className="etiqueta aviso">{noConformidades.total}</span></h3>
                <ul className="portal-atencion-lista">
                  {noConformidades.filas.map((n) => (
                    <li key={n.id}>
                      <Link to={`/portal/calidad/no-conformidades/${n.id}`}>{n.codigo}</Link>
                      <span>{n.titulo}</span>
                      <span className="pequena tenue">{n.centro} · {fecha(n.fechaApertura)}</span>
                      <EstadoNc estado={n.estado} />
                    </li>
                  ))}
                </ul>
                {noConformidades.total > noConformidades.filas.length && <Link to="/portal/calidad?tab=nc" className="pequena">Ver todas</Link>}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
